import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, Printer, Download } from 'lucide-react';
import { usePASClientAuth } from '../../context/PASClientAuthContext';
import { usePASAdminAuth } from '../../context/PASAdminAuthContext';
import { Form8824Sheet } from '../../components/tax/Form8824Sheet';
import '../../components/tax/form8824.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TaxFormDetail() {
  const navigate = useNavigate();
  const { transactionId, clientId } = useParams();
  const clientAuth = usePASClientAuth();
  const adminAuth = usePASAdminAuth();
  const isAdmin = !!clientId;
  const { token, loading } = isAdmin ? adminAuth : clientAuth;
  const user = isAdmin ? adminAuth.pasAdmin : clientAuth.pasClient;
  const base = isAdmin ? `${API}/pas-tax/admin/8824/${clientId}/${transactionId}` : `${API}/pas-tax/8824/${transactionId}`;
  const [form, setForm] = useState(null);
  const [fields, setFields] = useState(null);
  const [busy, setBusy] = useState('');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const apply = (data) => { setForm(data); setFields(data.fields); };
  const load = useCallback(() => axios.get(base, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => apply(r.data)).catch(() => toast.error('Form not found')), [token, base]);

  useEffect(() => { if (!loading && !user) navigate(isAdmin ? '/pas/admin/login' : '/pas/login'); }, [loading, user, isAdmin, navigate]);
  useEffect(() => { if (token) load(); }, [token, load]);

  const set = (k, v) => setFields(f => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy('save');
    try { const r = await axios.put(base, fields, auth); apply(r.data); toast.success('Form saved and recalculated', { id: 'f8824-saved' }); return true; }
    catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); return false; }
    finally { setBusy(''); }
  };

  const download = async () => {
    if (!isAdmin && !(await save())) return;
    setBusy('pdf');
    try {
      const r = await axios.post(`${base}/pdf`, {}, { ...auth, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `Form8824_${transactionId.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('PDF download failed'); }
    finally { setBusy(''); }
  };

  if (!form) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-slate-400">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-900 f8824-print-page">
      <header className="bg-slate-800 border-b border-slate-700 print:hidden">
        <div className="max-w-5xl mx-auto px-4 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(isAdmin ? '/pas/admin/dashboard?tab=tax-forms' : '/pas/tax-forms')} className="p-2 text-slate-400 hover:text-white" data-testid="tax-form-back-btn"><ArrowLeft className="w-5 h-5" /></button>
            <div>
              <h1 className="text-lg font-bold text-white">Form 8824</h1>
              <p className="text-xs text-slate-400" data-testid="f8824-mode-note">{isAdmin ? `Admin view of ${form.name}'s form. Read-only; the customer completes and saves it.` : 'Pre-filled from your transaction. Edit the white fields, then save, print or download.'}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!isAdmin && <button onClick={save} disabled={!!busy} data-testid="f8824-save-btn" className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm flex items-center gap-1"><Save className="w-4 h-4" /> Save</button>}
            <button onClick={() => window.print()} data-testid="f8824-print-btn" className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm flex items-center gap-1"><Printer className="w-4 h-4" /> Print</button>
            <button onClick={download} disabled={!!busy} data-testid="f8824-pdf-btn" className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium flex items-center gap-1"><Download className="w-4 h-4" /> {busy === 'pdf' ? 'Preparing...' : 'Official IRS PDF'}</button>
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-8 print:p-0">
        <Form8824Sheet form={form} fields={fields} set={set} readOnly={isAdmin} />
      </main>
    </div>
  );
}
